import type {
  BetBoxState,
  BetSide,
  BetStatus,
  HistoryItem,
  Phase,
} from '@platform/contracts';
import { formatCentsToCurrency } from '@core/utils/CurrencyFormatter';

export interface CrashTableIdentity {
  tenantId?: string | number;
  gameId?: string | number;
  tableId?: string | number;
}

export interface CrashSnapshotOwnBet {
  betId?: string;
  betNo?: number;
  status: 'ACTIVE' | 'CASHED_OUT' | 'CANCELLED' | 'BUSTED' | string;
  stake?: number;
  autoCashoutX?: number;
  autoCashoutEnabled?: boolean;
  cashoutMultiplier?: number;
  cashoutAmount?: number;
}

export interface CrashReconnectSnapshot {
  tenantId?: string | number;
  gameId?: string | number;
  tableId?: string | number;
  roundId?: string;
  roundNo?: number;
  serverPhase?: string;
  clientPhase: Phase;
  multiplier?: number;
  bust?: number;
  serverNow?: number;
  phaseStart?: number;
  phaseEnd?: number;
  totalDuration: number;
  remainingDuration: number;
  balance?: number;
  currency?: string;
  ownBets: CrashSnapshotOwnBet[];
  hasOwnBets: boolean;
  roundHistory: HistoryItem[];
  hasRoundHistory: boolean;
  liveBets: unknown[];
  hasLiveBets: boolean;
  activeBetPlayers?: number;
  activeBets?: number;
  totalBets?: number;
  totalCashoutAmount?: number;
}

const FINALIZED_OWN_STATUSES = new Set(['CASHED_OUT', 'CANCELLED', 'BUSTED']);
const IN_ROUND_CLIENT_STATUSES = new Set<BetStatus>([
  'placing',
  'placed',
  'cashing',
  'cashed',
  'lost',
  'cancelling',
]);

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function identitiesMatch(
  expected: string | number | undefined,
  actual: string | number | undefined
): boolean {
  if (expected == null || actual == null) return true;
  return String(expected) === String(actual);
}

export function validateSnapshotIdentity(
  snapshot: Pick<CrashReconnectSnapshot, 'tenantId' | 'gameId' | 'tableId'>,
  expected: CrashTableIdentity
): boolean {
  return (
    identitiesMatch(expected.tenantId, snapshot.tenantId) &&
    identitiesMatch(expected.gameId, snapshot.gameId) &&
    identitiesMatch(expected.tableId, snapshot.tableId)
  );
}

export function mapServerPhaseToClient(phase: unknown): Phase {
  const normalized = String(phase ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  switch (normalized) {
    case 'LOBBY':
    case 'WAITING':
      return 'WAITING';
    case 'BET_LOCK':
      return 'BET_LOCK';
    case 'TAKEOFF':
      return 'TAKEOFF';
    case 'RUNNING':
    case 'FLYING':
      return 'FLYING';
    case 'REVEAL':
      return 'REVEAL';
    case 'FLEW_AWAY':
    case 'CRASHED':
    case 'COMPLETED':
    case 'SETTLED':
      return 'FLEW_AWAY';
    default:
      return 'UNKNOWN';
  }
}

/** JOIN may send a display multiplier or an mbp integer (existing PF/history contract). */
export function parseJoinMultiplier(value: unknown): number | undefined {
  const n = toFiniteNumber(value);
  if (n == null || n <= 0) return undefined;
  if (n < 100) return n;
  if (Number.isInteger(n) && n >= 100_000) return n / 100_000;
  if (Number.isInteger(n) && n >= 1_000) {
    const scaled = n / 1_000;
    if (scaled >= 1) return scaled;
  }
  return n;
}

function firstMultiplier(
  payload: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const parsed = parseJoinMultiplier(payload[key]);
    if (parsed != null) return parsed;
  }
  return undefined;
}

/** Server money fields follow the existing JOIN/ACK cents contract. */
export function parseJoinMoney(value: unknown): number | undefined {
  const n = toFiniteNumber(value);
  if (n == null) return undefined;
  if (Number.isInteger(n)) return formatCentsToCurrency(n);
  return n;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

function parseOwnBet(raw: unknown): CrashSnapshotOwnBet | null {
  const entry = asRecord(raw);
  if (!entry) return null;

  const betNo = toFiniteNumber(entry.betNo ?? entry.betSlot ?? entry.betId);
  const betIdRaw = entry.betId ?? entry.id ?? entry.serverBetId;
  const status = String(entry.status ?? entry.betStatus ?? '')
    .trim()
    .toUpperCase();
  if (!status && betNo == null && betIdRaw == null) return null;

  const autoCashoutValue = parseJoinMultiplier(
    entry.autoCashoutX ??
      entry.autoCashout ??
      entry.autoCashoutThreshold ??
      entry.autoCashoutMul
  );
  const autoCashoutFlag =
    typeof entry.autoCashoutEnabled === 'boolean'
      ? entry.autoCashoutEnabled
      : typeof entry.autoCashout === 'boolean'
        ? entry.autoCashout
        : autoCashoutValue != null;

  return {
    betId: betIdRaw != null ? String(betIdRaw) : undefined,
    betNo: betNo != null && betNo > 0 ? Math.trunc(betNo) : undefined,
    status: status || 'ACTIVE',
    stake: parseJoinMoney(
      entry.stakeChips ?? entry.stake ?? entry.bet ?? entry.amount
    ),
    autoCashoutX: autoCashoutValue,
    autoCashoutEnabled: autoCashoutFlag && autoCashoutValue != null,
    cashoutMultiplier: parseJoinMultiplier(
      entry.cashoutMultiplier ?? entry.cashoutMul ?? entry.atX ?? entry.cashoutX
    ),
    cashoutAmount: parseJoinMoney(
      entry.cashoutAmount ?? entry.payout ?? entry.payoutChips ?? entry.win
    ),
  };
}

function mergeOwnBets(
  myRoundBets: unknown,
  myBets: unknown
): CrashSnapshotOwnBet[] {
  const byNo = new Map<number, CrashSnapshotOwnBet>();
  const byId = new Map<string, CrashSnapshotOwnBet>();
  const extras: CrashSnapshotOwnBet[] = [];

  const findExisting = (
    bet: CrashSnapshotOwnBet
  ): CrashSnapshotOwnBet | undefined => {
    if (bet.betNo != null && byNo.has(bet.betNo)) return byNo.get(bet.betNo);
    if (bet.betId && byId.has(bet.betId)) return byId.get(bet.betId);
    return undefined;
  };

  const remember = (bet: CrashSnapshotOwnBet) => {
    if (bet.betNo != null) byNo.set(bet.betNo, bet);
    if (bet.betId) byId.set(bet.betId, bet);
    if (bet.betNo == null && !bet.betId) extras.push(bet);
  };

  const primary = Array.isArray(myRoundBets) ? myRoundBets : [];
  primary.forEach(raw => {
    const parsed = parseOwnBet(raw);
    if (parsed) remember(parsed);
  });

  const fallback = Array.isArray(myBets) ? myBets : [];
  fallback.forEach(raw => {
    const parsed = parseOwnBet(raw);
    if (!parsed) return;
    const existing = findExisting(parsed);
    if (existing && FINALIZED_OWN_STATUSES.has(existing.status)) {
      return;
    }
    remember(
      existing
        ? { ...existing, ...parsed, status: parsed.status || existing.status }
        : { ...parsed, status: parsed.status || 'ACTIVE' }
    );
  });

  const unique = new Map<string, CrashSnapshotOwnBet>();
  for (const bet of [...byNo.values(), ...byId.values(), ...extras]) {
    const key =
      bet.betNo != null
        ? `no:${bet.betNo}`
        : bet.betId
          ? `id:${bet.betId}`
          : `extra:${unique.size}`;
    unique.set(key, bet);
  }
  return Array.from(unique.values());
}

function parseRoundHistory(raw: unknown): HistoryItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const entry = asRecord(item);
      if (!entry) return null;
      const bust =
        parseJoinMultiplier(
          entry.crashX ?? entry.bust ?? entry.multiplier ?? entry.bustX
        ) ?? 1;
      const roundId = entry.roundNo ?? entry.roundId ?? entry.id ?? `R${index}`;
      return {
        roundId: roundId as string | number,
        bust,
        timeStamp:
          toFiniteNumber(entry.ts ?? entry.endedAt ?? entry.timeStamp) ??
          Date.now(),
        serverSeed: String(entry.serverSeed ?? 'N/A'),
        serverSeedHash: String(entry.serverSeedHash ?? 'N/A'),
      } satisfies HistoryItem;
    })
    .filter((item): item is HistoryItem => item != null)
    .slice(0, 60);
}

function parseLiveBets(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(item => {
    const entry = asRecord(item);
    if (!entry) return item;
    return {
      ...entry,
      nickname: entry.nickname ?? entry.player ?? entry.nickName,
      bet: entry.bet ?? entry.stake ?? entry.stakeChips ?? entry.amount,
      betId: entry.betId ?? entry.betNo ?? entry.id,
      betNo: entry.betNo ?? entry.betId ?? entry.id,
      status: entry.status,
      cashoutMul: entry.cashoutMul ?? entry.cashoutMultiplier ?? entry.atX,
      cashoutAmount: entry.cashoutAmount ?? entry.payout ?? entry.payoutChips,
    };
  });
}

export function parseJoinSnapshot(
  payload: Record<string, unknown> | undefined,
  defaultDuration: number
): CrashReconnectSnapshot | null {
  if (!payload) return null;

  const nestedRound = {
    ...(asRecord(payload.roundState) ?? {}),
    ...(asRecord(payload.round) ?? {}),
  };
  const nestedTiming = {
    ...(asRecord(nestedRound.phaseTiming) ?? {}),
    ...(asRecord(payload.phaseTiming) ?? {}),
    ...(asRecord(payload.timing) ?? {}),
  };
  const nestedMetrics = {
    ...(asRecord(nestedRound.liveMetrics) ?? {}),
    ...(asRecord(payload.liveMetrics) ?? {}),
  };
  const source: Record<string, unknown> = {
    ...nestedRound,
    ...nestedTiming,
    ...nestedMetrics,
    ...payload,
  };
  const serverPhase = source.phase;
  const clientPhase = mapServerPhaseToClient(serverPhase);
  const phaseStart = toFiniteNumber(source.phaseStart);
  const phaseEnd = toFiniteNumber(
    source.phaseEnd ??
      source.lobbyEnd ??
      source.betLockEnd ??
      source.takeoffEnd ??
      source.revealEnd
  );
  const serverNow = toFiniteNumber(source.serverNow);
  const totalDuration =
    phaseStart != null && phaseEnd != null && phaseEnd > phaseStart
      ? phaseEnd - phaseStart
      : defaultDuration;
  let remainingDuration = totalDuration;
  if (phaseEnd != null && serverNow != null && phaseEnd > serverNow) {
    remainingDuration = phaseEnd - serverNow;
  } else if (phaseEnd != null && serverNow != null && phaseEnd <= serverNow) {
    remainingDuration = 0;
  }

  const roundIdValue = source.roundId ?? nestedRound.roundId;
  const multiplier = firstMultiplier(source, [
    'currentMultiplier',
    'currentX',
    'currentMbp',
    'nowX',
    'multiplier',
  ]);

  return {
    tenantId: (source.tenant ?? source.tenantId) as string | number | undefined,
    gameId: (source.game ?? source.gameId) as string | number | undefined,
    tableId: (source.table ?? source.tableId) as string | number | undefined,
    roundId: roundIdValue != null ? String(roundIdValue) : undefined,
    roundNo: toFiniteNumber(source.roundNo),
    serverPhase: serverPhase != null ? String(serverPhase) : undefined,
    clientPhase,
    multiplier,
    bust: parseJoinMultiplier(source.bust ?? source.crashX ?? source.bustX),
    serverNow,
    phaseStart,
    phaseEnd,
    totalDuration,
    remainingDuration,
    balance: parseJoinMoney(source.balance),
    currency: typeof source.currency === 'string' ? source.currency : undefined,
    ownBets: mergeOwnBets(source.myRoundBets, source.myBets),
    hasOwnBets:
      Array.isArray(source.myRoundBets) || Array.isArray(source.myBets),
    roundHistory: parseRoundHistory(source.roundHistory),
    hasRoundHistory: Array.isArray(source.roundHistory),
    liveBets: parseLiveBets(source.liveBets),
    hasLiveBets: Array.isArray(source.liveBets),
    activeBetPlayers: toFiniteNumber(
      source.activeBetPlayers ?? source.activePlayers
    ),
    activeBets: toFiniteNumber(source.activeBets),
    totalBets: toFiniteNumber(source.totalBets),
    totalCashoutAmount: parseJoinMoney(
      source.totalCashoutAmount ?? source.totalCashout
    ),
  };
}

function mapSnapshotStatusToClient(status: string): BetStatus {
  switch (String(status).toUpperCase()) {
    case 'CASHED_OUT':
      return 'cashed';
    case 'BUSTED':
      return 'lost';
    case 'CANCELLED':
      return 'idle';
    case 'ACTIVE':
    case 'PLACED':
      return 'placed';
    default:
      return 'idle';
  }
}

function resolveSideForBet(
  bet: CrashSnapshotOwnBet,
  sides: BetSide[],
  used: Set<BetSide>
): BetSide | undefined {
  if (bet.betNo === 1 && sides.includes('A') && !used.has('A')) return 'A';
  if (bet.betNo === 2 && sides.includes('B') && !used.has('B')) return 'B';
  const byIndex = sides[Math.max(0, (bet.betNo ?? 1) - 1)];
  if (byIndex && !used.has(byIndex)) return byIndex;
  return sides.find(side => !used.has(side));
}

export function buildOwnBetsFromSnapshot(
  snapshot: CrashReconnectSnapshot,
  currentBets: Record<BetSide, BetBoxState>,
  sides: BetSide[]
): Record<BetSide, BetBoxState> {
  const next: Record<BetSide, BetBoxState> = { ...currentBets };
  const used = new Set<BetSide>();

  for (const snapshotBet of snapshot.ownBets) {
    const side = resolveSideForBet(snapshotBet, sides, used);
    if (!side) continue;
    used.add(side);
    const current = currentBets[side];
    const status = mapSnapshotStatusToClient(snapshotBet.status);
    const autoCashoutEnabled = Boolean(
      snapshotBet.autoCashoutEnabled ?? current.autoCashoutEnabled
    );
    next[side] = {
      ...current,
      status,
      betNo: snapshotBet.betNo ?? current.betNo,
      serverBetId: snapshotBet.betId ?? current.serverBetId,
      stake: snapshotBet.stake ?? current.stake,
      autoCashoutX: snapshotBet.autoCashoutX ?? current.autoCashoutX,
      autoCashoutEnabled,
      // ACO lives on the Auto tab — after reload betMode defaults to 'bet',
      // which plus isTabSwitchDisabled locked users out of Auto intermittently.
      betMode: autoCashoutEnabled ? 'auto' : current.betMode,
      cashedAtX:
        status === 'cashed' ? snapshotBet.cashoutMultiplier : undefined,
      win: status === 'cashed' ? snapshotBet.cashoutAmount : undefined,
      error: '',
      placedAtX: status === 'placed' ? (snapshot.multiplier ?? 1) : undefined,
    };
  }

  for (const side of sides) {
    if (used.has(side)) continue;
    const current = currentBets[side];
    if (!IN_ROUND_CLIENT_STATUSES.has(current.status)) continue;
    next[side] = {
      ...current,
      status: 'idle',
      betNo: undefined,
      serverBetId: undefined,
      cashedAtX: undefined,
      win: undefined,
      error: '',
      placedAtX: undefined,
    };
  }

  return next;
}

export interface CrashSnapshotStore {
  getState: () => {
    bets: Record<BetSide, BetBoxState>;
    setRound: (roundId: string) => void;
    setTick: (x: number, ts: number) => void;
    setFinalX: (finalX: number) => void;
    setCurrency: (currency: string) => void;
    setBalance: (balance: number) => void;
    setPhaseDurations: (total: number, remaining: number) => void;
    setHistory?: (items: HistoryItem[]) => void;
    setServerBets: (serverBets: unknown[]) => void;
    setRoundStats: (stats: {
      activePlayers?: number;
      totalCashout?: number;
      activeBets?: number;
      totalBets?: number;
    }) => void;
    upBet: (side: BetSide, patch: Partial<BetBoxState>) => void;
  };
  setState?: (partial: {
    cancelledBetNos?: number[];
    cancelledBetStakes?: Array<{ stake: number; timestamp: number }>;
    history?: HistoryItem[];
    bets?: Record<BetSide, BetBoxState>;
  }) => void;
}

export function applyReconnectSnapshot(
  store: CrashSnapshotStore,
  snapshot: CrashReconnectSnapshot,
  sides: BetSide[]
): void {
  const state = store.getState();

  if (snapshot.roundId) {
    state.setRound(snapshot.roundId);
  }
  if (snapshot.multiplier != null) {
    state.setTick(snapshot.multiplier, snapshot.serverNow ?? Date.now());
  }
  if (snapshot.bust != null) {
    state.setFinalX(snapshot.bust);
  }
  state.setPhaseDurations(snapshot.totalDuration, snapshot.remainingDuration);
  if (snapshot.currency) {
    state.setCurrency(snapshot.currency);
  }
  if (snapshot.balance != null) {
    state.setBalance(snapshot.balance);
  }

  store.setState?.({
    cancelledBetNos: [],
    cancelledBetStakes: [],
  });

  if (snapshot.hasOwnBets) {
    const nextBets = buildOwnBetsFromSnapshot(snapshot, state.bets, sides);
    if (store.setState) {
      store.setState({ bets: nextBets });
    } else {
      for (const side of sides) {
        state.upBet(side, nextBets[side]);
      }
    }
  }

  if (snapshot.hasRoundHistory) {
    if (typeof state.setHistory === 'function') {
      state.setHistory(snapshot.roundHistory);
    } else {
      store.setState?.({ history: snapshot.roundHistory });
    }
  }

  if (snapshot.hasLiveBets) {
    state.setServerBets(snapshot.liveBets);
  }

  state.setRoundStats({
    activePlayers: snapshot.activeBetPlayers,
    activeBets: snapshot.activeBets,
    totalBets: snapshot.totalBets,
    totalCashout: snapshot.totalCashoutAmount,
  });
}
